"use client";

import { useEffect, useRef, useState } from "react";
import { DotLoader } from "@/components/ui/dot-loader";
import { cn } from "@/lib/utils";

export type DotFlowProps = {
  items: {
    title: string;
    frames: number[][];
    duration?: number;
    repeatCount?: number;
  }[];
  className?: string;
  variant?: "dark" | "purple";
  minWidth?: string;
};

export const DotFlow = ({ items, className, variant = "dark", minWidth }: DotFlowProps) => {
  const [index, setIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const next = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setIndex((prev) => (prev + 1) % items.length);
      setIsAnimating(false);
    }, 300);
  };

  const styles = {
    dark: {
      container: "bg-black",
      dot: "bg-white/15 [&.active]:bg-white size-1",
      text: "text-white",
    },
    purple: {
      container: "bg-gradient-to-r from-purple-600 to-purple-500 shadow-lg shadow-purple-500/25",
      dot: "bg-white/30 [&.active]:bg-white size-1",
      text: "text-white",
    },
  };

  const style = styles[variant];

  return (
    <div className={cn("flex items-center gap-3 rounded-full px-4 py-2.5", style.container, className)}>
      <DotLoader
        frames={items[index].frames}
        onComplete={next}
        className="gap-px"
        repeatCount={items[index].repeatCount ?? 1}
        duration={items[index].duration ?? 150}
        dotClassName={style.dot}
      />
      <div
        ref={containerRef}
        className="relative overflow-hidden"
        style={{ minWidth: minWidth }}
      >
        <div
          className={cn(
            "text-sm font-medium whitespace-nowrap transition-all duration-300",
            style.text,
            isAnimating ? "opacity-0 translate-y-2 blur-sm" : "opacity-100 translate-y-0 blur-0"
          )}
        >
          {items[index].title}
        </div>
      </div>
    </div>
  );
};
